import { Editor, JSONContent } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import type { Transaction } from "@tiptap/pm/state";
import {
  BetweenHorizontalStart, Heading1, Heading2, Heading3, ImagePlus, List, ListOrdered,
  Bold, Italic, Underline, Link2, RemoveFormatting, Plus, Quote, Rows3, Table2, Trash2, Type,
} from "lucide-react";
import { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { blocksToTiptap, tiptapToBlocks } from "./document-converter";
import { editorExtensions } from "./editor-extensions";
import { blockId, BlockFile, DocumentBlock } from "./types";
import { documentToText, textToDocument } from "../../lib/richtext";
import { useTranslation } from "react-i18next";
import "../../i18n";

type BlockEditorProps = {
  onUploadImage?: (file: File) => Promise<BlockFile>;
  loadImage?: (fileId: string) => Promise<Blob>;
  onError?: (message: string) => void;
  ariaLabel: string;
  id?: string;
  disabled?: boolean;
} & ({ format?: "blocks"; value: DocumentBlock[]; onChange: (blocks: DocumentBlock[]) => void }
  | { format: "markdown"; value: string; onChange: (text: string) => void });

type InsertAction = "paragraph" | "heading1" | "heading2" | "heading3" | "bullet" | "numbered" | "quote" | "table" | "image";

const insertActions: { id: InsertAction; label: string; shortcut: string; icon: typeof Type }[] = [
  { id: "paragraph", label: "본문", shortcut: "", icon: Type },
  { id: "heading1", label: "큰 제목", shortcut: "#", icon: Heading1 },
  { id: "heading2", label: "중간 제목", shortcut: "##", icon: Heading2 },
  { id: "heading3", label: "작은 제목", shortcut: "###", icon: Heading3 },
  { id: "bullet", label: "글머리 목록", shortcut: "-", icon: List },
  { id: "numbered", label: "번호 목록", shortcut: "1.", icon: ListOrdered },
  { id: "quote", label: "인용", shortcut: ">", icon: Quote },
  { id: "table", label: "표", shortcut: "/table", icon: Table2 },
  { id: "image", label: "이미지", shortcut: "/image", icon: ImagePlus },
];

function updateImageSource(editor: Editor, fileId: string, src: string) {
  const transaction = editor.state.tr;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.fileId === fileId && node.attrs.src !== src) {
      transaction.setNodeMarkup(pos, undefined, { ...node.attrs, src });
    }
  });
  if (transaction.steps.length) editor.view.dispatch(transaction.setMeta("image-source", true));
}

function normalizeEditedAiBlocks(editor: Editor, transaction: Transaction): boolean {
  if (!transaction.docChanged || transaction.getMeta("origin-normalized")) return false;
  const ranges: { from: number; to: number }[] = [];
  transaction.mapping.maps.forEach((map) => map.forEach((_oldFrom, _oldTo, from, to) => ranges.push({ from, to: Math.max(from + 1, to) })));
  const next = editor.state.tr;
  const touched = new Set<number>();
  for (const range of ranges) {
    editor.state.doc.nodesBetween(Math.max(0, range.from - 1), Math.min(editor.state.doc.content.size, range.to + 1), (node, pos) => {
      if (node.attrs.origin !== "AI" || touched.has(pos)) return;
      if (!["paragraph", "heading", "table"].includes(node.type.name)) return;
      touched.add(pos);
      next.setNodeMarkup(pos, undefined, { ...node.attrs, origin: "USER" });
    });
  }
  if (!next.steps.length) return false;
  editor.view.dispatch(next.setMeta("origin-normalized", true));
  return true;
}

export function BlockEditor(props: BlockEditorProps) {
  const { value, onUploadImage, loadImage, onError, ariaLabel, id, disabled = false } = props;
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const slashRange = useRef<{ from: number; to: number } | null>(null);
  const menuNavigated = useRef(false);
  const menu = useRef<HTMLDivElement>(null);
  const actions = insertActions.filter(action => (action.id !== "image" || onUploadImage)
    && (action.shortcut.replace(/^\//, "").startsWith(query) || action.label.includes(query)));
  const [uploading, setUploading] = useState(false);
  const [, renderSelection] = useState(0);
  const imageInput = useRef<HTMLInputElement>(null);
  const objectUrls = useRef(new Map<string, string>());
  const loadingImages = useRef(new Set<string>());
  const initialContent = useRef<JSONContent | null>(null);
  if (!initialContent.current) initialContent.current = typeof value === "string" ? textToDocument(value) : blocksToTiptap(value);
  const lastValue = useRef(value);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: editorExtensions,
    content: initialContent.current,
    editable: !disabled,
    editorProps: {
      attributes: { ...(id ? { id } : {}), role: "textbox", "aria-multiline": "true", "aria-label": ariaLabel, class: "notion-prosemirror" },
      handleKeyDown: (view, event) => {
        if (event.isComposing || view.composing || !menuOpen) return false;
        if (event.key === "Escape") { event.preventDefault(); setMenuOpen(false); slashRange.current = null; return true; }
        if (["ArrowDown", "ArrowUp"].includes(event.key) && actions.length) {
          event.preventDefault();
          menuNavigated.current = true;
          setSelected(index => (index + (event.key === "ArrowDown" ? 1 : -1) + actions.length) % actions.length);
          return true;
        }
        const action = event.key === " " ? actions.find(action => action.shortcut === `/${query}`)
          : event.key === "Enter" && (query || menuNavigated.current) ? actions[selected] : undefined;
        if (!action) return false;
        event.preventDefault(); run(action.id); return true;
      },
    },
    onUpdate: ({ editor: current, transaction }) => {
      if (transaction.getMeta("image-source")) return;
      if (normalizeEditedAiBlocks(current, transaction)) return;
      if (props.format === "markdown") {
        const text = current.isEmpty ? "" : documentToText(current.getJSON());
        lastValue.current = text;
        props.onChange(text);
      } else {
        const blocks = tiptapToBlocks(current.getJSON());
        lastValue.current = blocks;
        props.onChange(blocks);
      }
      updateSlashMenu(current);
      renderSelection((revision) => revision + 1);
    },
    onSelectionUpdate: ({ editor: current }) => { updateSlashMenu(current); renderSelection((revision) => revision + 1); },
    onFocus: () => renderSelection((revision) => revision + 1),
    onBlur: () => setMenuOpen(false),
  });

  useLayoutEffect(() => {
    if (!editor || value === lastValue.current) return;
    lastValue.current = value;
    if (typeof value === "string") {
      editor.commands.setContent(textToDocument(value), { emitUpdate: false });
      return;
    }
    const current = tiptapToBlocks(editor.getJSON());
    if (JSON.stringify(current) === JSON.stringify(value)) return;
    const imageSources = Object.fromEntries(objectUrls.current.entries());
    editor.commands.setContent(blocksToTiptap(value, imageSources), { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => { editor?.setEditable(!disabled, false); }, [editor, disabled]);

  useEffect(() => {
    if (!editor || !loadImage || typeof value === "string") return;
    for (const block of value) {
      if (block.type !== "IMAGE" || objectUrls.current.has(block.file.id) || loadingImages.current.has(block.file.id)) continue;
      loadingImages.current.add(block.file.id);
      void loadImage(block.file.id).then((blob) => {
        const url = URL.createObjectURL(blob);
        objectUrls.current.set(block.file.id, url);
        updateImageSource(editor, block.file.id, url);
      }).catch(() => onError?.(`${block.file.originalName} 이미지를 불러오지 못했습니다.`))
        .finally(() => loadingImages.current.delete(block.file.id));
    }
  }, [editor, loadImage, onError, value]);

  useEffect(() => () => { objectUrls.current.forEach((url) => URL.revokeObjectURL(url)); }, []);

  useLayoutEffect(() => {
    if (!menuOpen || !editor || !menu.current) return;
    const popup = menu.current;
    const host = popup.parentElement!.getBoundingClientRect();
    const caret = editor.view.coordsAtPos(editor.state.selection.from);
    const bounds = popup.closest(".modal")?.getBoundingClientRect();
    const below = (bounds?.bottom ?? window.innerHeight) - caret.bottom - 16;
    const above = caret.top - (bounds?.top ?? 0) - 16;
    const flip = below < 280 && above > below;
    popup.style.top = `${(flip ? caret.top - 6 : caret.bottom + 6) - host.top}px`;
    popup.style.left = `${Math.max(8, Math.min(caret.left - host.left, host.width - popup.offsetWidth - 8))}px`;
    popup.style.transform = flip ? "translateY(-100%)" : "none";
    popup.style.maxHeight = `${Math.max(120, flip ? above : below)}px`;
  }, [editor, menuOpen, query]);

  useEffect(() => { menu.current?.querySelector(".active")?.scrollIntoView({ block: "nearest" }); }, [selected]);

  function updateSlashMenu(current: Editor) {
    const { $from, empty } = current.state.selection;
    const prefix = $from.parent.textBetween(0, $from.parentOffset, "\n");
    const match = empty && /(?:^|\s)\/([a-z0-9]*)$/.exec(prefix);
    const nextQuery = match ? match[1] : "";
    slashRange.current = match ? { from: $from.pos - nextQuery.length - 1, to: $from.pos } : null;
    menuNavigated.current = false;
    setQuery(nextQuery); setSelected(0); setMenuOpen(!!match);
  }

  function run(action: InsertAction) {
    if (!editor) return;
    setMenuOpen(false);
    const chain = editor.chain().focus();
    if (slashRange.current) {
      chain.deleteRange(slashRange.current);
      slashRange.current = null;
    }
    if (action === "paragraph") chain.setParagraph().run();
    else if (action === "heading1") chain.toggleHeading({ level: 1 }).run();
    else if (action === "heading2") chain.toggleHeading({ level: 2 }).run();
    else if (action === "heading3") chain.toggleHeading({ level: 3 }).run();
    else if (action === "bullet") chain.toggleBulletList().run();
    else if (action === "numbered") chain.toggleOrderedList().run();
    else if (action === "quote") chain.toggleBlockquote().run();
    else if (action === "table") chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    else { chain.run(); imageInput.current?.click(); }
  }

  async function uploadImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !onUploadImage || !editor) return;
    setUploading(true);
    try {
      const stored = await onUploadImage(file);
      const src = URL.createObjectURL(file);
      objectUrls.current.set(stored.id, src);
      const content: JSONContent[] = [
        {
          type: "image",
          attrs: {
            src, alt: "", title: "", blockId: blockId(), origin: "USER", fileId: stored.id,
            originalName: stored.originalName, contentType: stored.contentType, sizeBytes: stored.sizeBytes, sha256: stored.sha256,
          },
        },
        { type: "paragraph", attrs: { blockId: blockId(), origin: "USER" } },
      ];
      // Stored image blocks are top-level: a table/list cell cannot own one.
      const { $from } = editor.state.selection;
      const chain = editor.chain().focus();
      if ($from.depth > 1) chain.insertContentAt($from.after(1), content).run();
      else chain.insertContent(content).run();
    } catch (reason) {
      onError?.(reason instanceof Error ? reason.message : "이미지를 업로드하지 못했습니다.");
    } finally { setUploading(false); }
  }

  if (!editor) return <div className="notion-editor-loading">편집기를 준비하고 있습니다.</div>;
  const tableActive = editor.isActive("table");
  const imageActive = editor.isActive("image");

  return <div className={`notion-editor${editor.isFocused ? " is-focused" : ""}`} inert={disabled}>
    <div className="notion-toolbar" role="toolbar" aria-label={t("editor.format", { label: ariaLabel })}>
      <button type="button" className="notion-add" aria-label="블록 추가" aria-expanded={menuOpen} onClick={() => { setQuery(""); setSelected(0); setMenuOpen(!menuOpen); }}><Plus size={15} /></button>
      <button type="button" title={t("editor.bold") + " (⌘/Ctrl+B)"} aria-label={t("editor.bold")} aria-pressed={editor.isActive("bold")} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></button>
      <button type="button" title={t("editor.italic") + " (⌘/Ctrl+I)"} aria-label={t("editor.italic")} aria-pressed={editor.isActive("italic")} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></button>
      <button type="button" title={t("editor.underline") + " (⌘/Ctrl+U)"} aria-label={t("editor.underline")} aria-pressed={editor.isActive("underline")} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleUnderline().run()}><Underline size={15} /></button>
      <label className="editor-color" title="글씨 색"><span aria-hidden="true">A</span><input type="color" aria-label="글씨 색" value={editor.getAttributes("textColor").color || "#171a21"} onChange={event => editor.chain().focus().setMark("textColor", { color: event.target.value }).run()} /></label>
      <button type="button" title={t("editor.link")} aria-label={t("editor.link")} aria-pressed={editor.isActive("link")} onMouseDown={event => event.preventDefault()} onClick={() => {
        const url = window.prompt(t("editor.linkPrompt"), editor.getAttributes("link").href || "https://");
        if (url === null) return;
        if (!url.trim()) editor.chain().focus().extendMarkRange("link").unsetLink().run();
        else if (/^https?:\/\/\S+$/i.test(url.trim())) editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
        else onError?.("http:// 또는 https://로 시작하는 링크를 입력해 주세요.");
      }}><Link2 size={15} /></button>
      <button type="button" title={t("editor.clear")} aria-label={t("editor.clear")} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><RemoveFormatting size={15} /></button>
      <i />
      <button type="button" className={editor.isActive("heading", { level: 1 }) ? "active" : ""} title="# + Space" aria-label={t("editor.largeTitle")} onClick={() => run("heading1")}><Heading1 size={15} /></button>
      <button type="button" className={editor.isActive("heading", { level: 2 }) ? "active" : ""} title="## + Space" aria-label="중간 제목" onClick={() => run("heading2")}><Heading2 size={15} /></button>
      <button type="button" className={editor.isActive("heading", { level: 3 }) ? "active" : ""} title="### + Space" aria-label="작은 제목" onClick={() => run("heading3")}><Heading3 size={15} /></button>
      <button type="button" className={editor.isActive("bulletList") ? "active" : ""} aria-label={t("editor.bulletList")} onClick={() => run("bullet")}><List size={15} /></button>
      <button type="button" className={editor.isActive("orderedList") ? "active" : ""} aria-label={t("editor.numberedList")} onClick={() => run("numbered")}><ListOrdered size={15} /></button>
      <button type="button" className={editor.isActive("blockquote") ? "active" : ""} aria-label="인용" onClick={() => run("quote")}><Quote size={15} /></button>
      <i />
      <button type="button" aria-label="표 삽입" onClick={() => run("table")}><Table2 size={15} /></button>
      {onUploadImage && <button type="button" aria-label="이미지 삽입" disabled={uploading} onClick={() => run("image")}><ImagePlus size={15} /></button>}
      <span>/ 로 빠르게 추가</span>
    </div>

    {menuOpen && <div ref={menu} className="notion-slash-menu" role="menu" aria-label="블록 메뉴">
      <header><b>블록 추가</b><small>줄 시작에 기호 + Space · ↑↓ 선택 · Enter 실행</small></header>
      <div>{actions.map((action, index) => <button type="button" role="menuitem" aria-label={`${action.label} ${action.shortcut}`.trim()} className={index === selected ? "active" : ""} key={action.id} onMouseDown={event => event.preventDefault()} onClick={() => run(action.id)}>
        <action.icon size={16} /><span>{action.label}</span>{action.shortcut && <kbd>{action.shortcut}</kbd>}
      </button>)}</div>
      {!actions.length && <p>일치하는 명령이 없습니다. 입력한 텍스트는 그대로 유지됩니다.</p>}
    </div>}

    <EditorContent editor={editor} />

    {tableActive && <div className="notion-context-bar" aria-label="표 편집 도구">
      <span><Table2 size={13} /> 표 편집</span>
      <input aria-label={`${ariaLabel} 표 설명`} value={String(editor.getAttributes("table").caption || "")} onChange={(event) => editor.chain().focus().updateAttributes("table", { caption: event.target.value, origin: "USER" }).run()} placeholder="표 설명" />
      <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()}><BetweenHorizontalStart size={13} /> 열 추가</button>
      <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()}><Rows3 size={13} /> 행 추가</button>
      <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()}>열 삭제</button>
      <button type="button" onClick={() => editor.chain().focus().deleteRow().run()}>행 삭제</button>
      <button type="button" className="danger" onClick={() => editor.chain().focus().deleteTable().run()}><Trash2 size={13} /> 표 삭제</button>
    </div>}

    {imageActive && <div className="notion-image-fields">
      <label>캡션<input aria-label={`${ariaLabel} 이미지 캡션`} value={String(editor.getAttributes("image").title || "")} onChange={(event) => editor.chain().focus().updateAttributes("image", { title: event.target.value, origin: "USER" }).run()} placeholder="이미지가 보여주는 내용을 설명하세요." /></label>
      <label>대체 텍스트<input aria-label={`${ariaLabel} 이미지 대체 텍스트`} value={String(editor.getAttributes("image").alt || "")} onChange={(event) => editor.chain().focus().updateAttributes("image", { alt: event.target.value, origin: "USER" }).run()} placeholder="접근성을 위한 설명" /></label>
    </div>}

    {onUploadImage && <input ref={imageInput} className="notion-hidden-file" type="file" accept="image/*" onChange={(event) => void uploadImage(event)} />}
  </div>;
}
